import React, { ReactNode, useState, useEffect, useCallback, useMemo } from "react";
import { authService } from "@/services/authService";
import { AuthContext, AuthContextType } from "@/hooks/useAuth";
import { useLazyGetHomeQuery } from "@/store/rest_api";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authHydrated, setAuthHydrated] = useState(false);

  const [fetchUser, { data: userData }] = useLazyGetHomeQuery();

  const refreshAuthStatus = useCallback(async () => {
    const token = await authService.getAccessToken();
    const authenticated = !!token;
    setIsAuthenticated(authenticated);

    if (authenticated) {
      // Trigger the query fetch
      await fetchUser();
    }
  }, [fetchUser]);

  const logout = useCallback(async () => {
    setIsAuthenticated(false);
    await authService.logout();
  }, []);

  useEffect(() => {
    (async () => {
      await refreshAuthStatus();
      setAuthHydrated(true);
    })();

    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === "AccessToken" || e.key === "RefreshToken") {
        await refreshAuthStatus();
      }
    };

    const handleCustomStorageEvent = async () => {
      await refreshAuthStatus();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("storage", handleCustomStorageEvent);

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        await refreshAuthStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("storage", handleCustomStorageEvent);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshAuthStatus]);

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated,
      authHydrated,
      user: userData || null,
      logout,
      refreshAuthStatus,
    }),
    [isAuthenticated, authHydrated, userData, logout, refreshAuthStatus],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
